import { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import {
  Activity,
  ArrowLeft,
  Clock,
  KeyRound,
  Loader2,
  ScrollText,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserCheck,
  UserMinus
} from 'lucide-react';

import { useAuth } from '@chainlit/react-client';

import { apiClient, type AdminUserDetail, type AdminUserSummary } from '@/api';
import TempAccountCard from './TempAccountCard';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  adminUserDetailDialogOpenState,
  adminUserDetailIdentifierState,
  adminUsersState,
  auditLogDialogOpenState,
  auditLogUserIdentifierState
} from '@/state/project';

import { formatTimestamp as formatDate } from '@/lib/format';
import AuditEventTable from './AuditEventTable';
import UserTokenUsageSection from './UserTokenUsageSection';

export default function UserDetailDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useRecoilState(adminUserDetailDialogOpenState);
  const [identifier, setIdentifier] = useRecoilState(adminUserDetailIdentifierState);
  const setUsers = useSetRecoilState(adminUsersState);
  const setAuditIdentifier = useSetRecoilState(auditLogUserIdentifierState);
  const setAuditOpen = useSetRecoilState(auditLogDialogOpenState);
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [busy, setBusy] = useState<'role' | 'disabled' | 'reset' | 'delete' | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const isSelf = !!(user?.identifier && identifier && user.identifier === identifier);

  const refresh = useCallback(async () => {
    if (!identifier) return;
    setIsLoading(true);
    try {
      const data = await apiClient.getAdminUser(identifier);
      setDetail(data);
    } catch (error) {
      console.error('Failed to load user detail', error);
      toast.error('Failed to load user');
      setDetail(null);
    } finally {
      setIsLoading(false);
    }
  }, [identifier]);

  useEffect(() => {
    if (open && identifier) {
      refresh();
    } else {
      setDetail(null);
    }
  }, [open, identifier, refresh]);

  const handleClose = (next: boolean) => {
    if (!next) {
      setIdentifier(null);
    }
    setOpen(next);
  };

  const handleSearchUserEvents = () => {
    if (!detail) return;
    setAuditIdentifier(detail.identifier);
    setAuditOpen(true);
    handleClose(false);
  };

  const applyToList = useCallback(
    (updated: Partial<AdminUserSummary> & { identifier: string }) => {
      setUsers((prev) =>
        prev.map((row) => (row.identifier === updated.identifier ? { ...row, ...updated } : row))
      );
    },
    [setUsers]
  );

  const handleToggleAdmin = async () => {
    if (!detail) return;
    const nextRole = detail.role === 'admin' ? 'user' : 'admin';
    setBusy('role');
    try {
      const updated = await apiClient.setAdminUserRole(detail.identifier, nextRole);
      setDetail({ ...detail, role: updated.role });
      applyToList(updated);
      toast.success(`${detail.identifier} → ${nextRole}`);
    } catch (error: any) {
      toast.error(error?.message || 'Role update failed');
    } finally {
      setBusy(null);
    }
  };

  const handleToggleDisabled = async () => {
    if (!detail) return;
    setBusy('disabled');
    try {
      const updated = await apiClient.setAdminUserDisabled(detail.identifier, !detail.disabled);
      setDetail({ ...detail, disabled: updated.disabled });
      applyToList(updated);
      toast.success(`${detail.identifier} ${updated.disabled ? 'disabled' : 'enabled'}`);
    } catch (error: any) {
      toast.error(error?.message || 'Disable update failed');
    } finally {
      setBusy(null);
    }
  };

  const handlePasswordReset = async () => {
    if (!detail) return;
    setBusy('reset');
    try {
      const result = await apiClient.triggerAdminPasswordReset(detail.identifier);
      toast.success(`Password reset email sent to ${result.email}`);
    } catch (error: any) {
      toast.error(error?.message || 'Password reset failed');
    } finally {
      setBusy(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!detail) return;
    setBusy('delete');
    try {
      await apiClient.deleteAdminUser(detail.identifier);
      setUsers((prev) => prev.filter((row) => row.identifier !== detail.identifier));
      toast.success(`Deleted ${detail.identifier}`);
      setConfirmDeleteOpen(false);
      handleClose(false);
    } catch (error: any) {
      toast.error(error?.message || 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="w-[92vw] max-w-[1400px] h-[85vh] min-h-0 sm:min-h-[600px] flex flex-col gap-6 p-6 sm:p-8 box-border overflow-hidden"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between gap-4 pr-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleClose(false)}
              className="gap-2 hover:bg-primary/10 transition-colors shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Users
            </Button>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSearchUserEvents}
                disabled={!detail}
                className="gap-1 hover:bg-primary/10 hover:border-primary/50 transition-colors"
                title="Open the audit log filtered to this user's events"
              >
                <ScrollText className="h-4 w-4" />
                Search user events
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                disabled={isLoading}
                className="hover:bg-primary/10 hover:border-primary/50 transition-colors"
              >
                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
          </div>

          {isLoading && !detail ? (
            <div className="space-y-6">
              <Skeleton className="h-12 w-1/2" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : !detail ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              No user selected.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xl font-bold ring-2 ring-background">
                  {(detail.identifier[0] || '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold tracking-tight truncate">
                    {detail.identifier}
                  </h1>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {isSelf ? (
                      <Badge variant="outline" className="text-[10px]">you</Badge>
                    ) : null}
                    {detail.role === 'admin' ? (
                      <Badge className="text-[10px]">admin</Badge>
                    ) : null}
                    {detail.disabled ? (
                      <Badge variant="destructive" className="text-[10px]">disabled</Badge>
                    ) : null}
                    {detail.id.startsWith('pending:') ? (
                      <Badge variant="secondary" className="text-[10px]">pending first login</Badge>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex flex-1 min-h-0 flex-col lg:flex-row gap-6 overflow-y-auto lg:overflow-visible">
                <div className="lg:flex-1 flex flex-col min-w-0 lg:min-h-0">
                  <div className="flex items-center gap-3 mb-4">
                    <Activity className="h-5 w-5 text-primary" />
                    <h3 className="text-xl font-bold">Activity</h3>
                  </div>
                  <div className="lg:flex-1 lg:min-h-0 rounded-xl p-4 sm:p-6 border-2 shadow-inner lg:overflow-y-auto space-y-4 bg-muted/40">
                    {detail.last_login_at ? (
                      <Card className="bg-card border-2">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base">Last login</CardTitle>
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>{formatDate(detail.last_login_at)}</span>
                              </div>
                              {detail.last_login_ip ? (
                                <div className="text-xs text-muted-foreground mt-1">
                                  from <code>{detail.last_login_ip}</code>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ) : (
                      <Card className="bg-card border-2 border-dashed">
                        <CardContent className="p-4 text-sm text-muted-foreground">
                          No login recorded yet for this account.
                        </CardContent>
                      </Card>
                    )}
                    <UserTokenUsageSection identifier={detail.identifier} />
                    <Card className="bg-card border-2">
                      <Accordion type="single" collapsible>
                        <AccordionItem value="audit-trail" className="border-none">
                          <AccordionTrigger className="px-4 text-sm font-semibold hover:no-underline">
                            Audit trail
                          </AccordionTrigger>
                          <AccordionContent className="px-4">
                            <AuditEventTable
                              filters={{ user_identifier: detail.identifier, limit: 50 }}
                              mode="dialog"
                            />
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </Card>
                  </div>
                </div>

                <div className="w-full lg:w-[380px] lg:shrink-0 flex flex-col space-y-6 lg:overflow-y-auto">
                  <Card className="border-2 shadow-lg">
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-primary" />
                        <CardTitle className="text-lg">Details</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <DetailRow label="Provider" value={detail.provider || '—'} />
                      <DetailRow label="Created" value={formatDate(detail.createdAt)} />
                      <DetailRow label="Last login" value={formatDate(detail.last_login_at)} />
                      <DetailRow label="Last IP" value={detail.last_login_ip || '—'} />
                      <DetailRow label="Email" value={detail.email || '—'} />
                      <Separator />
                      <DetailRow
                        label="Credential row"
                        value={detail.has_password ? 'yes' : 'no'}
                      />
                      {isSelf ? (
                        <p className="text-xs text-muted-foreground pt-2">
                          This is your own account.
                        </p>
                      ) : null}
                    </CardContent>
                  </Card>

                  {!isSelf && (
                    <TempAccountCard
                      detail={detail}
                      onUpdated={(next) => {
                        setDetail(next);
                        applyToList({
                          identifier: next.identifier,
                          is_temporary: next.is_temporary,
                          temp_expires_at: next.temp_expires_at
                        });
                      }}
                    />
                  )}

                  <Card className="border-2 shadow-lg">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-primary" />
                        <CardTitle className="text-base">Actions</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-xs justify-start"
                        onClick={handleToggleAdmin}
                        disabled={busy !== null || (isSelf && detail.role === 'admin')}
                        title={detail.role === 'admin' ? 'Demote to user' : 'Promote to admin'}
                      >
                        {busy === 'role' ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : detail.role === 'admin' ? (
                          <UserMinus className="h-3 w-3 mr-1" />
                        ) : (
                          <UserCheck className="h-3 w-3 mr-1" />
                        )}
                        <span className="truncate">
                          {detail.role === 'admin' ? 'Demote' : 'Promote'}
                        </span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-xs justify-start"
                        onClick={handleToggleDisabled}
                        disabled={busy !== null || isSelf}
                        title={detail.disabled ? 'Enable account' : 'Disable account'}
                      >
                        {busy === 'disabled' ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : detail.disabled ? (
                          <ShieldCheck className="h-3 w-3 mr-1" />
                        ) : (
                          <ShieldOff className="h-3 w-3 mr-1" />
                        )}
                        <span className="truncate">
                          {detail.disabled ? 'Enable' : 'Disable'}
                        </span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-xs justify-start"
                        onClick={handlePasswordReset}
                        disabled={busy !== null}
                        title="Send password reset email"
                      >
                        {busy === 'reset' ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <KeyRound className="h-3 w-3 mr-1" />
                        )}
                        <span className="truncate">Reset</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-xs justify-start text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"
                        onClick={() => setConfirmDeleteOpen(true)}
                        disabled={busy !== null || isSelf}
                        title="Delete user"
                      >
                        {busy === 'delete' ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <Trash2 className="h-3 w-3 mr-1" />
                        )}
                        <span className="truncate">Delete</span>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {detail?.identifier}?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes the Chainlit user row, the credential row, and the OAuth
              whitelist entry. Threads and chat history orphan but remain in the
              database (`userId` set to NULL). Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'delete'}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={busy === 'delete'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy === 'delete' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-right break-all">{value}</span>
    </div>
  );
}
